import type { QueryClient } from '@tanstack/react-query';
import type {
  SellerSupportMessage,
  SellerSupportMessagesResponse,
  SellerSupportThreadsResponse,
} from './types';

export function chatMessagesQueryKey(sellerId: number) {
  return ['seller-support-chat', 'messages', sellerId] as const;
}

export const CHAT_THREADS_QUERY_KEY = ['seller-support-chat', 'threads'] as const;
export const CHAT_UNREAD_QUERY_KEY = ['seller-support-chat', 'unread-count'] as const;

let activeAdminChatSellerId: number | null = null;

export function setActiveAdminChatSellerId(sellerId: number | null) {
  activeAdminChatSellerId = sellerId;
}

export function getActiveAdminChatSellerId() {
  return activeAdminChatSellerId;
}

export function mergeSellerSupportMessages(
  prev: SellerSupportMessagesResponse | undefined,
  next: SellerSupportMessagesResponse,
): SellerSupportMessagesResponse {
  if (!prev?.items.length) return next;
  const byId = new Map(prev.items.map((item) => [item.id, item]));
  for (const item of next.items) byId.set(item.id, item);
  const items = [...byId.values()].sort((a, b) => {
    const byTime = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    return byTime !== 0 ? byTime : a.id - b.id;
  });
  return {
    threadId: next.threadId || prev.threadId,
    items,
    total: Math.max(next.total, items.length),
  };
}

/** Поднять тред в начало списка диалогов при новом сообщении. */
function bumpSellerThreadInList(
  queryClient: QueryClient,
  sellerId: number,
  message: SellerSupportMessage,
  meta?: { shopName?: string | null; shopLogoUrl?: string | null },
) {
  queryClient.setQueryData<SellerSupportThreadsResponse>(CHAT_THREADS_QUERY_KEY, (prev) => {
    if (!prev) return prev;
    const existing = prev.items.find((item) => item.sellerId === sellerId);
    const isActive = activeAdminChatSellerId === sellerId;
    const unreadDelta = !isActive && message.senderKind === 'SELLER' ? 1 : 0;
    const thread = existing
      ? {
          ...existing,
          lastMessage: message,
          updatedAt: message.createdAt,
          unreadCount: existing.unreadCount + unreadDelta,
        }
      : {
          threadId: message.threadId,
          sellerId,
          shopName: meta?.shopName?.trim() || `Продавец #${sellerId}`,
          shopLogoUrl: meta?.shopLogoUrl ?? null,
          lastMessage: message,
          unreadCount: unreadDelta,
          updatedAt: message.createdAt,
        };
    const without = prev.items.filter((item) => item.sellerId !== sellerId);
    return {
      ...prev,
      items: [thread, ...without],
      total: existing ? prev.total : prev.total + 1,
    };
  });
}

export function appendSellerSupportMessageToCache(
  queryClient: QueryClient,
  sellerId: number,
  message: SellerSupportMessage,
  meta?: { shopName?: string | null; shopLogoUrl?: string | null },
) {
  queryClient.setQueryData<SellerSupportMessagesResponse>(
    chatMessagesQueryKey(sellerId),
    (prev) => {
      const base = prev ?? { items: [], total: 0, threadId: message.threadId };
      if (base.items.some((item) => item.id === message.id)) return base;
      return {
        ...base,
        threadId: message.threadId || base.threadId,
        items: [...base.items, message],
        total: base.total + 1,
      };
    },
  );
  bumpSellerThreadInList(queryClient, sellerId, message, meta);
  void queryClient.invalidateQueries({ queryKey: CHAT_THREADS_QUERY_KEY });
}
